/**
 * DesignPropertiesPanel — extracted from AdvancedFormBuilder.js
 * Archive: /formbuilder/archive/AdvancedFormBuilder.v1.full.js (3232 lines)
 *
 * Renders the "Design Properties" inspector pane for a selected form element.
 * All mutations are handled via callbacks passed from AdvancedFormBuilder.
 */
import React, { useRef } from 'react';

export function DesignPropertiesPanel({
  selectedElementData,
  isGlobal,
  onUpdateAlignment,
  onUpdateStyle,
  onElementBackgroundUpload,
  onRemoveElementBackground,
  onApplyPreset,
}) {
  const isSelectedElementGlobal = isGlobal;
  const elementBackgroundInputRef = useRef(null);

  const updateElementAlignment = (id, alignment, textAlign, g) => onUpdateAlignment(id, alignment, textAlign, g);
  const updateElementStyle    = (id, updates, g) => onUpdateStyle(id, updates, g);
  const handleElementBackgroundUpload = (id, file, g) => onElementBackgroundUpload(id, file, g);
  const removeElementBackgroundImage  = (id, g) => onRemoveElementBackground(id, g);
  const applyStylePreset      = (preset, id, g) => onApplyPreset(preset, id, g);

  if (!selectedElementData) return null;

  const isPercentage = selectedElementData.styling.width?.includes('%');
  const widthValue = selectedElementData.styling.width?.replace('%', '').replace('px', '') || '100';
  const heightValue = selectedElementData.styling.height?.replace('px', '') || '100';

  return (
    <div className="properties-group">
      <h4>Design Properties {isSelectedElementGlobal && <span className="global-badge">🌍 Global</span>}</h4>

      {/* Layout & Positioning */}
      <div className="property-section">
        <h5>📐 Layout & Positioning</h5>

        {/* Dynamic Alignment Controls */}
        <div className="property-group">
          <label>Element Alignment:</label>
          <div className="alignment-controls">
            <button
              className={`align-btn ${selectedElementData.alignment === 'left' ? 'active' : ''}`}
              onClick={() => updateElementAlignment(selectedElementData.id, 'left', 'left', isSelectedElementGlobal)}
              title="Align Left"
            >
              ⬅️
            </button>
            <button
              className={`align-btn ${selectedElementData.alignment === 'center' ? 'active' : ''}`}
              onClick={() => updateElementAlignment(selectedElementData.id, 'center', 'center', isSelectedElementGlobal)}
              title="Align Center"
            >
              ⬜
            </button>
            <button
              className={`align-btn ${selectedElementData.alignment === 'right' ? 'active' : ''}`}
              onClick={() => updateElementAlignment(selectedElementData.id, 'right', 'right', isSelectedElementGlobal)}
              title="Align Right"
            >
              ➡️
            </button>
          </div>
        </div>

        {/* Dynamic Size Controls */}
        <div className="property-row">
          <div className="property-group">
            <label>Width Type:</label>
            <select
              value={selectedElementData.styling.width?.includes('%') ? 'percentage' :
                selectedElementData.styling.width?.includes('px') ? 'pixels' : 'auto'}
              onChange={(e) => {
                const widthType = e.target.value;
                let newWidth = '100%';

                if (widthType === 'pixels') newWidth = '300px';
                if (widthType === 'auto') newWidth = 'auto';
                if (widthType === 'percentage') newWidth = '100%';

                updateElementStyle(selectedElementData.id, { width: newWidth }, isSelectedElementGlobal);
              }}
            >
              <option value="percentage">Percentage (%)</option>
              <option value="pixels">Fixed (px)</option>
              <option value="auto">Auto</option>
            </select>
          </div>

          <div className="property-group">
            <label>Width Value:</label>
            <input
              type={selectedElementData.styling.width?.includes('%') ? 'number' : 'text'}
              value={widthValue}
              onChange={(e) => {
                const value = e.target.value;
                const isPercentage = selectedElementData.styling.width?.includes('%');
                const newWidth = isPercentage ? `${value}%` : `${value}px`;
                updateElementStyle(selectedElementData.id, { width: newWidth }, isSelectedElementGlobal);
              }}
              min={isPercentage ? "0" : "50"}
              max={isPercentage ? "100" : "1000"}
              step={isPercentage ? "5" : "10"}
            />
            <span className="unit-display">
              {selectedElementData.styling.width?.includes('%') ? '%' :
                selectedElementData.styling.width?.includes('px') ? 'px' : ''}
            </span>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Height Type:</label>
            <select
              value={selectedElementData.styling.height?.includes('px') ? 'fixed' : 'auto'}
              onChange={(e) => {
                const heightType = e.target.value;
                let newHeight = 'auto';

                if (heightType === 'fixed') newHeight = '100px';
                if (heightType === 'auto') newHeight = 'auto';

                updateElementStyle(selectedElementData.id, { height: newHeight }, isSelectedElementGlobal);
              }}
            >
              <option value="auto">Auto</option>
              <option value="fixed">Fixed (px)</option>
            </select>
          </div>

          {selectedElementData.styling.height?.includes('px') && (
            <div className="property-group">
              <label>Height Value:</label>
              <input
                type="number"
                value={heightValue}
                onChange={(e) => {
                  const newHeight = `${e.target.value}px`;
                  updateElementStyle(selectedElementData.id, { height: newHeight }, isSelectedElementGlobal);
                }}
                min="20"
                max="1000"
                step="10"
              />
              <span className="unit-display">px</span>
            </div>
          )}
        </div>

        {/* Responsive Size Controls */}
        <div className="property-section">
          <h6>📱 Responsive Sizes</h6>
          <div className="property-row">
            <div className="property-group">
              <label>Mobile Width:</label>
              <input
                type="text"
                value={selectedElementData.styling.mobileWidth || '100%'}
                onChange={(e) => updateElementStyle(selectedElementData.id, { mobileWidth: e.target.value }, isSelectedElementGlobal)}
                placeholder="100%"
              />
            </div>
            <div className="property-group">
              <label>Tablet Width:</label>
              <input
                type="text"
                value={selectedElementData.styling.tabletWidth || '100%'}
                onChange={(e) => updateElementStyle(selectedElementData.id, { tabletWidth: e.target.value }, isSelectedElementGlobal)}
                placeholder="100%"
              />
            </div>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Display:</label>
            <select
              value={selectedElementData.styling.display || 'block'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { display: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="block">Block</option>
              <option value="inline-block">Inline Block</option>
              <option value="inline">Inline</option>
              <option value="flex">Flex</option>
              <option value="grid">Grid</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="property-group">
            <label>Position:</label>
            <select
              value={selectedElementData.styling.position || 'static'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { position: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="static">Static</option>
              <option value="relative">Relative</option>
              <option value="absolute">Absolute</option>
              <option value="fixed">Fixed</option>
              <option value="sticky">Sticky</option>
            </select>
          </div>
        </div>

        {/* Margin Controls */}
        <div className="property-group">
          <label>Margin:</label>
          <div className="spacing-controls">
            <div className="spacing-input-group">
              <input
                type="text"
                placeholder="Top"
                value={selectedElementData.styling.marginTop || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { marginTop: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Right"
                value={selectedElementData.styling.marginRight || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { marginRight: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Bottom"
                value={selectedElementData.styling.marginBottom || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { marginBottom: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Left"
                value={selectedElementData.styling.marginLeft || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { marginLeft: e.target.value }, isSelectedElementGlobal)}
              />
            </div>
          </div>
        </div>

        {/* Padding Controls */}
        <div className="property-group">
          <label>Padding:</label>
          <div className="spacing-controls">
            <div className="spacing-input-group">
              <input
                type="text"
                placeholder="Top"
                value={selectedElementData.styling.paddingTop || selectedElementData.styling.padding || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { paddingTop: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Right"
                value={selectedElementData.styling.paddingRight || selectedElementData.styling.padding || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { paddingRight: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Bottom"
                value={selectedElementData.styling.paddingBottom || selectedElementData.styling.padding || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { paddingBottom: e.target.value }, isSelectedElementGlobal)}
              />
              <input
                type="text"
                placeholder="Left"
                value={selectedElementData.styling.paddingLeft || selectedElementData.styling.padding || ''}
                onChange={(e) => updateElementStyle(selectedElementData.id, { paddingLeft: e.target.value }, isSelectedElementGlobal)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="property-section">
        <h5>🔤 Typography</h5>
        <div className="property-row">
          <div className="property-group">
            <label>Font Family:</label>
            <select
              value={selectedElementData.styling.fontFamily || 'inherit'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { fontFamily: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="inherit">Inherit</option>
              <option value="Arial, sans-serif">Arial</option>
              <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Georgia', serif">Georgia</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="'Comic Sans MS', cursive">Comic Sans</option>
              <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
              <option value="'Verdana', sans-serif">Verdana</option>
            </select>
          </div>
          <div className="property-group">
            <label>Font Size:</label>
            <input
              type="text"
              value={selectedElementData.styling.fontSize || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { fontSize: e.target.value }, isSelectedElementGlobal)}
              placeholder="14px"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Font Weight:</label>
            <select
              value={selectedElementData.styling.fontWeight || 'normal'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { fontWeight: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="lighter">Lighter</option>
              <option value="bolder">Bolder</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="300">300</option>
              <option value="400">400</option>
              <option value="500">500</option>
              <option value="600">600</option>
              <option value="700">700</option>
              <option value="800">800</option>
              <option value="900">900</option>
            </select>
          </div>
          <div className="property-group">
            <label>Line Height:</label>
            <input
              type="text"
              value={selectedElementData.styling.lineHeight || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { lineHeight: e.target.value }, isSelectedElementGlobal)}
              placeholder="1.5"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Text Align:</label>
            <select
              value={selectedElementData.styling.textAlign || 'left'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { textAlign: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </div>
          <div className="property-group">
            <label>Text Transform:</label>
            <select
              value={selectedElementData.styling.textTransform || 'none'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { textTransform: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="none">None</option>
              <option value="uppercase">Uppercase</option>
              <option value="lowercase">Lowercase</option>
              <option value="capitalize">Capitalize</option>
            </select>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Letter Spacing:</label>
            <input
              type="text"
              value={selectedElementData.styling.letterSpacing || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { letterSpacing: e.target.value }, isSelectedElementGlobal)}
              placeholder="0px"
            />
          </div>
          <div className="property-group">
            <label>Text Decoration:</label>
            <select
              value={selectedElementData.styling.textDecoration || 'none'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { textDecoration: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="none">None</option>
              <option value="underline">Underline</option>
              <option value="overline">Overline</option>
              <option value="line-through">Line Through</option>
            </select>
          </div>
        </div>
      </div>

      {/* Colors & Background */}
      <div className="property-section">
        <h5>🎨 Colors & Background</h5>
        <div className="property-row">
          <div className="property-group">
            <label>Text Color:</label>
            <input
              type="color"
              value={selectedElementData.styling.color || '#000000'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { color: e.target.value }, isSelectedElementGlobal)}
            />
          </div>
          <div className="property-group">
            <label>Background Color:</label>
            <div className="background-color-controls">
              <input
                type="color"
                value={selectedElementData.styling.backgroundColor || '#ffffff'}
                onChange={(e) => updateElementStyle(selectedElementData.id, { backgroundColor: e.target.value }, isSelectedElementGlobal)}
              />
              <button
                className="no-color-btn"
                onClick={() => updateElementStyle(selectedElementData.id, { backgroundColor: 'transparent' }, isSelectedElementGlobal)}
                title="No Color"
                aria-label="Remove background color"
              >
                🚫 No Color
              </button>
            </div>
          </div>
        </div>

        <div className="property-group">
          <label>Background Image:</label>
          <div className="background-image-controls">
            <input
              type="text"
              value={selectedElementData.styling.backgroundImage || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { backgroundImage: e.target.value }, isSelectedElementGlobal)}
              placeholder="url('image.jpg')"
              className="background-image-input"
            />
            <div className="background-image-actions">
              <button
                type="button"
                className="upload-background-btn"
                onClick={() => elementBackgroundInputRef.current?.click()}
              >
                📁 Upload
              </button>
              {selectedElementData.styling.backgroundImage && (
                <button
                  type="button"
                  className="remove-background-btn"
                  onClick={() => removeElementBackgroundImage(selectedElementData.id, isSelectedElementGlobal)}
                >
                  🗑️ Remove
                </button>
              )}
            </div>
          </div>
          <input
            ref={elementBackgroundInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleElementBackgroundUpload(selectedElementData.id, file, isSelectedElementGlobal);
              }
            }}
          />
        </div>

        {selectedElementData.styling.backgroundImage && (
          <div className="property-group">
            <label>Background Preview:</label>
            <div
              className="background-preview"
              style={{
                backgroundImage: selectedElementData.styling.backgroundImage,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '100px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0'
              }}
            />
          </div>
        )}

        <div className="property-row">
          <div className="property-group">
            <label>Background Size:</label>
            <select
              value={selectedElementData.styling.backgroundSize || 'auto'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { backgroundSize: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="auto">Auto</option>
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="100% 100%">100% 100%</option>
            </select>
          </div>
          <div className="property-group">
            <label>Background Repeat:</label>
            <select
              value={selectedElementData.styling.backgroundRepeat || 'repeat'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { backgroundRepeat: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="repeat">Repeat</option>
              <option value="no-repeat">No Repeat</option>
              <option value="repeat-x">Repeat X</option>
              <option value="repeat-y">Repeat Y</option>
            </select>
          </div>
        </div>
      </div>

      {/* Borders */}
      <div className="property-section">
        <h5>🟦 Borders</h5>
        <div className="property-row">
          <div className="property-group">
            <label>Border Width:</label>
            <input
              type="text"
              value={selectedElementData.styling.borderWidth || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { borderWidth: e.target.value }, isSelectedElementGlobal)}
              placeholder="1px"
            />
          </div>
          <div className="property-group">
            <label>Border Style:</label>
            <select
              value={selectedElementData.styling.borderStyle || 'solid'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { borderStyle: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
              <option value="groove">Groove</option>
              <option value="ridge">Ridge</option>
              <option value="inset">Inset</option>
              <option value="outset">Outset</option>
            </select>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Border Color:</label>
            <input
              type="color"
              value={selectedElementData.styling.borderColor || '#e2e8f0'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { borderColor: e.target.value }, isSelectedElementGlobal)}
            />
          </div>
          <div className="property-group">
            <label>Border Radius:</label>
            <input
              type="text"
              value={selectedElementData.styling.borderRadius || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { borderRadius: e.target.value }, isSelectedElementGlobal)}
              placeholder="8px"
            />
          </div>
        </div>

        {/* Individual Border Controls */}
        <div className="property-group">
          <label>Individual Borders:</label>
          <div className="border-controls">
            <div className="border-input-group">
              <div className="border-input">
                <span>Top</span>
                <input
                  type="text"
                  placeholder="1px"
                  value={selectedElementData.styling.borderTopWidth || ''}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderTopWidth: e.target.value }, isSelectedElementGlobal)}
                />
                <input
                  type="color"
                  value={selectedElementData.styling.borderTopColor || selectedElementData.styling.borderColor || '#e2e8f0'}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderTopColor: e.target.value }, isSelectedElementGlobal)}
                />
              </div>
              <div className="border-input">
                <span>Right</span>
                <input
                  type="text"
                  placeholder="1px"
                  value={selectedElementData.styling.borderRightWidth || ''}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderRightWidth: e.target.value }, isSelectedElementGlobal)}
                />
                <input
                  type="color"
                  value={selectedElementData.styling.borderRightColor || selectedElementData.styling.borderColor || '#e2e8f0'}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderRightColor: e.target.value }, isSelectedElementGlobal)}
                />
              </div>
              <div className="border-input">
                <span>Bottom</span>
                <input
                  type="text"
                  placeholder="1px"
                  value={selectedElementData.styling.borderBottomWidth || ''}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderBottomWidth: e.target.value }, isSelectedElementGlobal)}
                />
                <input
                  type="color"
                  value={selectedElementData.styling.borderBottomColor || selectedElementData.styling.borderColor || '#e2e8f0'}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderBottomColor: e.target.value }, isSelectedElementGlobal)}
                />
              </div>
              <div className="border-input">
                <span>Left</span>
                <input
                  type="text"
                  placeholder="1px"
                  value={selectedElementData.styling.borderLeftWidth || ''}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderLeftWidth: e.target.value }, isSelectedElementGlobal)}
                />
                <input
                  type="color"
                  value={selectedElementData.styling.borderLeftColor || selectedElementData.styling.borderColor || '#e2e8f0'}
                  onChange={(e) => updateElementStyle(selectedElementData.id, { borderLeftColor: e.target.value }, isSelectedElementGlobal)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Effects & Animation */}
      <div className="property-section">
        <h5>✨ Effects & Animation</h5>
        <div className="property-row">
          <div className="property-group">
            <label>Box Shadow:</label>
            <input
              type="text"
              value={selectedElementData.styling.boxShadow || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { boxShadow: e.target.value }, isSelectedElementGlobal)}
              placeholder="0 2px 10px rgba(0,0,0,0.1)"
            />
          </div>
          <div className="property-group">
            <label>Opacity:</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={selectedElementData.styling.opacity || 1}
              onChange={(e) => updateElementStyle(selectedElementData.id, { opacity: e.target.value }, isSelectedElementGlobal)}
            />
            <span className="range-value">{(selectedElementData.styling.opacity || 1) * 100}%</span>
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Transform:</label>
            <input
              type="text"
              value={selectedElementData.styling.transform || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { transform: e.target.value }, isSelectedElementGlobal)}
              placeholder="rotate(5deg) scale(1.1)"
            />
          </div>
          <div className="property-group">
            <label>Transition:</label>
            <input
              type="text"
              value={selectedElementData.styling.transition || ''}
              onChange={(e) => updateElementStyle(selectedElementData.id, { transition: e.target.value }, isSelectedElementGlobal)}
              placeholder="all 0.3s ease"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Cursor:</label>
            <select
              value={selectedElementData.styling.cursor || 'default'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { cursor: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="default">Default</option>
              <option value="pointer">Pointer</option>
              <option value="text">Text</option>
              <option value="move">Move</option>
              <option value="not-allowed">Not Allowed</option>
              <option value="help">Help</option>
              <option value="wait">Wait</option>
              <option value="crosshair">Crosshair</option>
            </select>
          </div>
          <div className="property-group">
            <label>Overflow:</label>
            <select
              value={selectedElementData.styling.overflow || 'visible'}
              onChange={(e) => updateElementStyle(selectedElementData.id, { overflow: e.target.value }, isSelectedElementGlobal)}
            >
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
              <option value="scroll">Scroll</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced CSS */}
      <div className="property-section">
        <h5>⚡ Advanced CSS</h5>
        <div className="property-group">
          <label>Custom CSS:</label>
          <textarea
            value={selectedElementData.styling.customCSS || ''}
            onChange={(e) => updateElementStyle(selectedElementData.id, { customCSS: e.target.value }, isSelectedElementGlobal)}
            placeholder="Add custom CSS here..."
            rows="4"
          />
        </div>
      </div>

      {/* Style Presets */}
      <div className="property-section">
        <h5>🎭 Quick Style Presets</h5>
        <div className="style-presets-grid">
          <button
            className="style-preset primary"
            onClick={() => applyStylePreset('primary', selectedElementData.id, isSelectedElementGlobal)}
          >
            Primary
          </button>
          <button
            className="style-preset secondary"
            onClick={() => applyStylePreset('secondary', selectedElementData.id, isSelectedElementGlobal)}
          >
            Secondary
          </button>
          <button
            className="style-preset success"
            onClick={() => applyStylePreset('success', selectedElementData.id, isSelectedElementGlobal)}
          >
            Success
          </button>
          <button
            className="style-preset danger"
            onClick={() => applyStylePreset('danger', selectedElementData.id, isSelectedElementGlobal)}
          >
            Danger
          </button>
          <button
            className="style-preset warning"
            onClick={() => applyStylePreset('warning', selectedElementData.id, isSelectedElementGlobal)}
          >
            Warning
          </button>
          <button
            className="style-preset info"
            onClick={() => applyStylePreset('info', selectedElementData.id, isSelectedElementGlobal)}
          >
            Info
          </button>
        </div>
      </div>
    </div>
  );
}

export default DesignPropertiesPanel;
